// modals/TaskList.js - VERSION CORRIGÉE AVEC NOUVEAUX STATUTS
import { useState } from 'react';
import TaskItem from './TaskItem';

// Options de priorité disponibles
const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Basse', color: 'bg-gray-100 text-gray-700' },
    { value: 'medium', label: 'Moyenne', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'high', label: 'Haute', color: 'bg-red-100 text-red-700' }
];

// Options de statut mises à jour avec les nouveaux statuts
const STATUS_OPTIONS = [
    { value: 'a_faire', label: 'À faire', color: 'bg-gray-100 text-gray-700' },
    { value: 'en_cours', label: 'En cours', color: 'bg-blue-100 text-blue-700' },
    { value: 'fait', label: 'Fait', color: 'bg-green-100 text-green-700' },
    { value: 'annule', label: 'Annulé', color: 'bg-red-100 text-red-700' }
];

const TaskList = ({ tasks, onUpdateTasks, errors, defaultExpanded = false, disabled = false }) => {
    // Modification ici : Set vide par défaut au lieu de new Set([0, 1])
    const [expandedTasks, setExpandedTasks] = useState(new Set());
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);
    const [dragType, setDragType] = useState(null);

    function createNewSubtask(title = '') {
        return {
            id: Date.now() + Math.random(),
            title,
            status: 'a_faire', // CORRECTION: Nouveau statut par défaut
            priority: 'medium' // Ajout de la priorité par défaut
        };
    }

    function handleTaskChange(taskIndex, field, value) {
        if (disabled) return; // Vérification du disabled

        const newTasks = tasks.map((task, index) =>
            index === taskIndex ? { ...task, [field]: value } : task
        );
        onUpdateTasks(newTasks);
    }

    function handleSubtaskChange(taskIndex, subtaskIndex, field, value) {
        if (disabled) return; // Vérification du disabled

        const newTasks = tasks.map((task, tIndex) =>
            tIndex === taskIndex
                ? {
                    ...task,
                    subtasks: task.subtasks.map((subtask, sIndex) =>
                        sIndex === subtaskIndex ? { ...subtask, [field]: value } : subtask
                    )
                }
                : task
        );
        onUpdateTasks(newTasks);
    }

    function removeTask(taskIndex) {
        if (disabled || tasks.length <= 1) return; // Vérification du disabled et minimum de tâches

        const newTasks = tasks.filter((_, index) => index !== taskIndex);
        onUpdateTasks(newTasks);
        setExpandedTasks(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskIndex);
            return newSet;
        });
    }

    function addSubtask(taskIndex) {
        if (disabled) return; // Vérification du disabled

        const newSubtask = createNewSubtask(`Sous-action ${tasks[taskIndex].subtasks.length + 1}`);
        const newTasks = tasks.map((task, index) =>
            index === taskIndex
                ? { ...task, subtasks: [...task.subtasks, newSubtask] }
                : task
        );
        onUpdateTasks(newTasks);
    }

    function removeSubtask(taskIndex, subtaskIndex) {
        if (disabled) return; // Vérification du disabled

        const newTasks = tasks.map((task, index) =>
            index === taskIndex
                ? {
                    ...task,
                    subtasks: task.subtasks.filter((_, sIndex) => sIndex !== subtaskIndex)
                }
                : task
        );
        onUpdateTasks(newTasks);
    }

    function toggleTaskExpansion(taskIndex) {
        setExpandedTasks(prev => {
            const newSet = new Set(prev);
            if (newSet.has(taskIndex)) {
                newSet.delete(taskIndex);
            } else {
                newSet.add(taskIndex);
            }
            return newSet;
        });
    }

    function reorderTasks(startIndex, endIndex) {
        if (disabled) return; // Vérification du disabled

        const newTasks = [...tasks];
        const [removed] = newTasks.splice(startIndex, 1);
        newTasks.splice(endIndex, 0, removed);
        onUpdateTasks(newTasks);
        setExpandedTasks(prev => {
            const newSet = new Set();
            prev.forEach(index => {
                if (index === startIndex) {
                    newSet.add(endIndex);
                } else if (index > startIndex && index <= endIndex) {
                    newSet.add(index - 1);
                } else if (index < startIndex && index >= endIndex) {
                    newSet.add(index + 1);
                } else {
                    newSet.add(index);
                }
            });
            return newSet;
        });
    }

    function reorderSubtasks(taskIndex, startIndex, endIndex) {
        if (disabled) return; // Vérification du disabled

        const newTasks = tasks.map((task, tIndex) => {
            if (tIndex === taskIndex) {
                const newSubtasks = [...task.subtasks];
                const [removed] = newSubtasks.splice(startIndex, 1);
                newSubtasks.splice(endIndex, 0, removed);
                return { ...task, subtasks: newSubtasks };
            }
            return task;
        });
        onUpdateTasks(newTasks);
    }

    function handleTaskDragStart(e, taskIndex) {
        if (disabled) return; // Vérification du disabled

        setDraggedItem({ taskIndex });
        setDragType('task');
        e.dataTransfer.effectAllowed = 'move';
    }

    function handleTaskDragOver(e, taskIndex) {
        if (disabled) return; // Vérification du disabled

        e.preventDefault();
        if (dragType === 'task') {
            e.dataTransfer.dropEffect = 'move';
            setDragOverItem({ taskIndex });
        }
    }

    function handleTaskDrop(e, taskIndex) {
        if (disabled) return; // Vérification du disabled

        e.preventDefault();
        if (draggedItem && dragType === 'task') {
            reorderTasks(draggedItem.taskIndex, taskIndex);
        }
        setDraggedItem(null);
        setDragOverItem(null);
        setDragType(null);
    }

    function handleSubtaskDragStart(e, taskIndex, subtaskIndex) {
        if (disabled) return; // Vérification du disabled

        e.stopPropagation();
        setDraggedItem({ taskIndex, subtaskIndex });
        setDragType('subtask');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', '');
    }

    function handleSubtaskDragOver(e, taskIndex, subtaskIndex) {
        if (disabled) return; // Vérification du disabled

        e.preventDefault();
        e.stopPropagation();
        if (dragType === 'subtask' && draggedItem?.taskIndex === taskIndex) {
            e.dataTransfer.dropEffect = 'move';
            setDragOverItem({ taskIndex, subtaskIndex });
        }
    }

    function handleSubtaskDrop(e, taskIndex, subtaskIndex) {
        if (disabled) return; // Vérification du disabled

        e.preventDefault();
        e.stopPropagation();
        if (draggedItem && dragType === 'subtask' && draggedItem.taskIndex === taskIndex) {
            if (draggedItem.subtaskIndex !== subtaskIndex) {
                reorderSubtasks(taskIndex, draggedItem.subtaskIndex, subtaskIndex);
            }
        }
        setDraggedItem(null);
        setDragOverItem(null);
        setDragType(null);
    }

    function handleDragEnd() {
        setDraggedItem(null);
        setDragOverItem(null);
        setDragType(null);
    }

    return (
        <div className="space-y-2">
            {tasks.map((task, taskIndex) => (
                <TaskItem
                    key={task.id}
                    task={task}
                    taskIndex={taskIndex}
                    isExpanded={expandedTasks.has(taskIndex)}
                    onTaskChange={handleTaskChange}
                    onSubtaskChange={handleSubtaskChange}
                    onRemoveTask={removeTask}
                    onAddSubtask={addSubtask}
                    onRemoveSubtask={removeSubtask}
                    onToggleExpansion={toggleTaskExpansion}
                    onTaskDragStart={handleTaskDragStart}
                    onTaskDragOver={handleTaskDragOver}
                    onTaskDrop={handleTaskDrop}
                    onSubtaskDragStart={handleSubtaskDragStart}
                    onSubtaskDragOver={handleSubtaskDragOver}
                    onSubtaskDrop={handleSubtaskDrop}
                    onDragEnd={handleDragEnd}
                    dragState={{
                        draggedItem,
                        dragOverItem,
                        dragType
                    }}
                    errors={errors}
                    canRemove={tasks.length > 1}
                    priorityOptions={PRIORITY_OPTIONS}
                    statusOptions={STATUS_OPTIONS} // Ajout des options de statut
                    disabled={disabled} // Passage du disabled au TaskItem
                />
            ))}
        </div>
    );
};

export default TaskList;